import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  AdvancedEstimationInterval,
  AdvancedEstimationResponse,
  AdvancedEstimationStatus,
  AdvancedVEEService,
  MeteredDataService,
  MeterStandType,
  ObservationTimeSeriesPeriod,
  QuantityQuality,
} from '@app/api/dmc';
import { FormApiService } from '@app/core/services/form-api.service';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { getDaysBetween } from '@app/core/utils/get-days-between';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { DatePipe } from '@angular/common';
import { AlertService } from '@app/core/services/alert.service';
import { dateRangeValidator } from '@shared/validators/date-range.validator';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { MINIMUM_YEAR } from '@app/core/constants/minimum-year';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';

@Component({
  selector: 'app-advanced-submit-timeseries-modal',
  imports: [
    ReactiveFormsModule,
    InputGroupComponent,
    LabelDirective,
    InputDirective,
    SelectDirective,
    TranslatePipe,
    ButtonDirective,
    DatePipe,
    InputDateDirective
  ],
  templateUrl: './advanced-submit-timeseries-modal.component.html',
  styleUrl: './advanced-submit-timeseries-modal.component.css'
})
export class AdvancedSubmitTimeseriesModalComponent implements OnInit, HasUnsavedChanges {
  // SubmitManualMeteredDataDto
  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public form: FormGroup = new FormGroup({
    meteringPointIdentification: new FormControl(null, Validators.required),
    quantityQuality: new FormControl(QuantityQuality.Estimated, Validators.required),
    wantsDataLocked: new FormControl(false),
    meteredData: new FormControl([], Validators.required)
  });

  /** Value form **/
  public valueForm: FormGroup = new FormGroup({
    start: new FormControl(this.getYesterdayDate(), [Validators.required]),
    end: new FormControl(this.getTodayDate(), [Validators.required]),
    periodUsage: new FormControl(0, [Validators.required]),
  });

  private getTodayDate(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  private getYesterdayDate(): string {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  }

  /** Interval Form **/
  public intervalForm: FormGroup = new FormGroup({});
  /** Interval Array **/
  public intervalArray: number[][] = [];
  /** Column Array **/
  public columns: string[] = [];
  /** Loading state for estimation **/
  public isEstimating: boolean = false;

  /** Dropdown values **/
  public quantityQuality: KeyValue[] = enumToKeyValue(QuantityQuality, { omitKeys: ['NotSpecified', 'Revised', 'Calculated'], prefixValue: 'meteringpoint.timeseries.' });

  /** ID **/
  public resolution?: ObservationTimeSeriesPeriod;
  public id?: string;

  constructor(
    private readonly meteredDataService: MeteredDataService,
    private readonly advancedVEEService: AdvancedVEEService,
    private readonly formApiService: FormApiService, 
    private readonly alertService: AlertService
  ) {
    if (this.id) {
      this.form.get('meteringPointIdentification')?.setValue(this.id);
    }

    // Add date range validators
    this.valueForm.get('start')?.addValidators(dateRangeValidator('start', 'end', 1, 10));
    this.valueForm.get('end')?.addValidators(dateRangeValidator('start', 'end', 1, 10));

    this.valueForm.get('start')?.valueChanges.subscribe((date: any) => {
      const d = new Date(date);
      if (d && d.getFullYear() >= MINIMUM_YEAR) {
        this.valueForm.get('end')?.updateValueAndValidity({ emitEvent: false });
        this.updateTableSize();
      }
    })

    this.valueForm.get('end')?.valueChanges.subscribe((date: any) => {
      const d = new Date(date);
      if (d && d.getFullYear() >= MINIMUM_YEAR)  {
        this.valueForm.get('start')?.updateValueAndValidity({ emitEvent: false });
        this.updateTableSize();
      }
    })
    
  }

  public ngOnInit(): void {
    if (this.resolution === undefined) {
      this.resolution = ObservationTimeSeriesPeriod.Quarter;
    }
    if (this.id) {
      this.form.get('meteringPointIdentification')?.setValue(this.id);
    }
    this.updateTableSize();
  }

  public clearValues(): void {
    const newValues: { [key: string]: number } = {};

    for (const array of this.intervalArray) {
      for (const element of array) {
        newValues[this.formatFormControlName(element)] = 0;
      }
    }
    this.intervalForm.patchValue(newValues);

    this.form.get('meteredData')?.setValue(this.generateMeteredData());
  }

  public calculateDateFromDayIndex(dayIndex: number): Date {
    const startDate = new Date(this.valueForm.get('start')?.value);
    const date = new Date(startDate);
    date.setDate(date.getDate() + dayIndex);
    return date;
  }

  public updateTableSize(): void {
    const startValue = this.valueForm.get('start')?.value;
    const endValue = this.valueForm.get('end')?.value;

    const daysBetween = getDaysBetween(startValue, endValue);

    this.intervalForm = new FormGroup({});
    this.intervalArray = [];
    this.columns = [];

    if (startValue === null || endValue === null || this.resolution === null) {
      return;
    }

    // Check for errors
    if (this.valueForm.get('start')?.invalid || this.valueForm.get('end')?.invalid) {
      return;
    }

    if (this.resolution === ObservationTimeSeriesPeriod.Hour) {
      this.updateTableSizeToHour(daysBetween);
    }
    else if (this.resolution === ObservationTimeSeriesPeriod.Quarter)
    {
      this.updateTableSizeToQuarter(daysBetween);
    }
  }

  public updateTableSizeToHour(dayCount: number): void {
    // Show 24 rows for each day (1 per hour)
    this.columns = ["Værdi"];
    this.intervalArray = new Array(24 * dayCount).fill(0).map((_, i) => [i]);

    for (const hourArray of this.intervalArray) {
      for (const hour of hourArray) {
        /** Add controls for each hour dynamically **/
        this.intervalForm.addControl(this.formatFormControlName(hour), new FormControl(0, Validators.required));
      }
    }
  }

  public updateTableSizeToQuarter(dayCount: number): void {
    // Show 96 rows for each day (4 per hour)
    this.columns = ["00-15", "15-30", "30-45", "45-00"];
    this.intervalArray = new Array(24 * dayCount).fill(0).map((_, i) => [i, i + 0.25, i + 0.5, i + 0.75]);

    for (const quarterArray of this.intervalArray) {
      for (const quarter of quarterArray) {
        /** Add controls for each quarter dynamically **/
        this.intervalForm.addControl(this.formatFormControlName(quarter), new FormControl(0, Validators.required));
      }
    }
  }

  public formatFormControlName(value: number): string {
    return `interval_${value.toFixed(2).replace('.', '_')}`;
  }

  public estimateValues(): void {
    this.alertService.close('modal');

    let totalQuantiy: number | null = this.valueForm.get('periodUsage')?.value;
    if (!totalQuantiy || totalQuantiy <= 0) {
      totalQuantiy = null;
    }

    // Always request quarter resolution
    let timeSeriesInterval: AdvancedEstimationInterval;
    switch (this.resolution) {
        case ObservationTimeSeriesPeriod.Hour:
            timeSeriesInterval = AdvancedEstimationInterval.Hour;
            break;
        case ObservationTimeSeriesPeriod.Quarter:
            timeSeriesInterval = AdvancedEstimationInterval.Quarter;
            break;
        default:
            this.alertService.open('modal', 'danger', 'error.timeseries.invalidresolution', true);
            return;
    }

    this.isEstimating = true;

    this.advancedVEEService.advancedVEEEstimateMeteringPointPost({
      meteringPointIdentification: this.id!,
      timeGapStart: this.valueForm.get('start')?.value,
      timeGapEnd: this.valueForm.get('end')?.value,
      totalQuantiy: totalQuantiy,
      timeSeriesInterval: timeSeriesInterval
    }).
    subscribe({
      next: (data: AdvancedEstimationResponse) => {
        if (data.status == AdvancedEstimationStatus.Success) {

          const newValues: { [key: string]: number } = {};

          if (data.timeSeries) {
            for (const element of data.timeSeries) {
              if (element.timeStamp && element.quantity) {
                const date = new Date(element.timeStamp);
                const startDate = new Date(this.valueForm.get('start')?.value);
                const dayIndex = getDaysBetween(startDate, date);
                const hourIndex = date.getHours();

                let controlName: string;
                if (this.resolution === ObservationTimeSeriesPeriod.Hour) {
                  controlName = this.formatFormControlName(dayIndex * 24 + hourIndex);
                } else {
                  const quarterIndex = Math.floor(date.getMinutes() / 15);
                  controlName = this.formatFormControlName(dayIndex * 24 + hourIndex + quarterIndex * 0.25);
                }
                newValues[controlName] = Number.parseFloat(element.quantity.toFixed(3));
              }
            }
          }

          this.intervalForm.patchValue(newValues);
          this.isEstimating = false;
        } else {
          this.alertService.open('modal', 'danger', 'error.timeseries.estimationfailed', true)
          this.isEstimating = false;
        }
      },
      error: () => {
        this.alertService.open('modal', 'danger', 'error.timeseries.estimationfailed', true)
        this.isEstimating = false;
      }
    })
  }

  public generateMeteredData(): { readingType: MeterStandType, readingTs: string, consumption: string }[] {
    const start = new Date(this.valueForm.get('start')?.value);
    const end = new Date(this.valueForm.get('end')?.value);
    const mappedReadingType = this.mapQuantityQualityToReadingType(this.form.get('quantityQuality')?.value);

    const days = getDaysBetween(start, end);

    const values: number[] = [];
    for (const array of this.intervalArray) {
      for (const element of array) {
        values.push(this.intervalForm.get(this.formatFormControlName(element))?.value ?? 0);
      }
    }

    if (this.resolution === ObservationTimeSeriesPeriod.Hour) {
      return this.generateHourlyMeteredData(days, start, end, mappedReadingType, values);
    } else if (this.resolution === ObservationTimeSeriesPeriod.Quarter) {
      return this.generateQuarterlyMeteredData(days, start, end, mappedReadingType, values);
    }
    return [];
  }

  public generateHourlyMeteredData(days: number, start: Date, end: Date, readingType: any, hourlyValues: number[]): { readingType: MeterStandType, readingTs: string, consumption: string }[] {
    const result: { readingType: MeterStandType, readingTs: string, consumption: string }[] = [];

    for (let d = 0; d < days; d++) {
      for (let h = 0; h < 24; h++) {
        const date = new Date(start);
        date.setDate(start.getDate() + d);
        date.setHours(h, 0, 0, 0);

        result.push({
          readingType: readingType,
          readingTs: date.toISOString(),
          consumption: hourlyValues[d * 24 + h].toFixed(3).toString()
        });
      }
    }

    return result;
  }
  public generateQuarterlyMeteredData(days: number, start: Date, end: Date, readingType: any, quarterlyValues: number[]): { readingType: MeterStandType, readingTs: string, consumption: string }[] {
    const result: { readingType: MeterStandType, readingTs: string, consumption: string }[] = [];

    for (let d = 0; d < days; d++) {
      for (let h = 0; h < 24; h++) {
        for (let q = 0; q < 4; q++) {
          const date = new Date(start);
          date.setDate(start.getDate() + d);
          date.setHours(h, q * 15, 0, 0);

          result.push({
            readingType: readingType,
            readingTs: date.toISOString(),
            consumption: quarterlyValues[d * 96 + h * 4 + q].toFixed(3).toString()
          });
        }
      }
    }

    return result;
  }
  public submit(): void {
    this.form.get('meteringPointIdentification')?.setValue(this.id);

    this.form.get('meteredData')?.setValue(this.generateMeteredData());

    this.form.get('wantsDataLocked')?.setValue(JSON.parse(this.form.value.wantsDataLocked));

    // Call for all other estimation methods
    this.formApiService.submit(
      this.form,
      (data) => this.meteredDataService.meteredDataSubmitManualMeteredDataPost(data),
      {
        successMessage: 'meteringpoint.timeseries.manualsubmit.success',
        closeModal: true,
      }
    )
  }

  private mapQuantityQualityToReadingType(quantityQuality?: QuantityQuality | string | null): MeterStandType | null {
    if (quantityQuality == null) return null;

    const mapping: Record<string, MeterStandType> = {
      NotSpecified: MeterStandType.NotSpecified,
      Estimated: MeterStandType.Estimated,
      AsRead: MeterStandType.Ok
    };

    const key = String(quantityQuality);
    return mapping[key] ?? null;
  }
}
